import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import environment from "../../environment/environment";
import "../UpcomingTripsDetails/UpcomingTripsDetails.css";
import HeaderComponent from "../../components/Header/Header";
import NewNavBar from "../../components/NewNavBar/NewNavBar";
import Sidebar from "../../components/Sidebar/Sidebar";

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

const CurrencyFormatter = new Intl.NumberFormat("en-IN");
const formatINR = (value) => `₹${CurrencyFormatter.format(value)}`;

const MONTH_ABBR = {
  jan: "Jan", feb: "Feb", mar: "Mar", apr: "Apr", may: "May", jun: "Jun",
  jul: "Jul", aug: "Aug", sep: "Sep", oct: "Oct", nov: "Nov", dec: "Dec",
};

// "Aug-2026" -> "Aug-26"  (matches the compact tab label in the design)
function formatMonthTabLabel(monthKey) {
  const [mon, year] = monthKey.split("-");
  const abbr = MONTH_ABBR[mon?.slice(0, 3).toLowerCase()] || (mon || "");
  return `${abbr}-${(year || "").slice(-2)}`;
}

// "Aug-2026" -> sortable Date
function monthKeyToDate(monthKey) {
  const [mon, year] = monthKey.split("-");
  return new Date(`01 ${mon} ${year}`);
}

// Every FUTURE (>= today) departure date for a package, scoped to one month/year bucket.
// This mirrors the logic used on the Upcoming Trips home-page carousel so date +
// batch counts stay consistent across the app.
function extractDatesForMonth(pkg, monthKey) {
  const seen = new Map();
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  let targetMonthAbbr = null;
  let targetYear = null;
  if (monthKey) {
    const [mon, year] = monthKey.split("-");
    targetMonthAbbr = (mon || "").slice(0, 3).toLowerCase();
    targetYear = year;
  }

  (pkg.departureDetails || []).forEach((dep) => {
    (dep.packageDateDetails || []).forEach((d) => {
      const key = d.fullDate || `${d.packageDate}-${d.packageMonth}-${d.packageYear}`;
      if (seen.has(key)) return;

      if (targetMonthAbbr && targetYear) {
        const dMonthAbbr = (d.packageMonth || "").slice(0, 3).toLowerCase();
        const dYear = String(d.packageYear || "");
        if (dMonthAbbr !== targetMonthAbbr || dYear !== targetYear) return;
      }

      const sortKey = new Date(`${d.packageDate} ${d.packageMonth} ${d.packageYear}`);
      if (sortKey >= today) {
        seen.set(key, {
          label: `${d.packageDate} ${(d.packageMonth || "").slice(0, 3)}`,
          sortKey,
        });
      }
    });
  });

  return Array.from(seen.values()).sort((a, b) => a.sortKey - b.sortKey);
}

// Raw API package -> the shape TripCard renders.
function mapPackageToTrip(pkg, monthKey) {
  const allDates = extractDatesForMonth(pkg, monthKey);
  const shownDates = allDates.slice(0, 1).map((d) => d.label);
  const extraBatches = Math.max(allDates.length - shownDates.length, 0);

  const cities = Array.from(
    new Set((pkg.departureDetails || []).map((d) => d.cityName).filter(Boolean))
  );

  return {
    id: pkg.packageUID,
    image: pkg.imageList?.[0]?.coverImage,
    title: (pkg.packageName || "").trim(),
    originalPrice:
      pkg.packagePrices && pkg.packagePrices !== pkg.packageWithDiscountPrices
        ? pkg.packagePrices
        : undefined,
    price: pkg.packageWithDiscountPrices ?? pkg.packagePrices ?? 0,
    duration: `${pkg.packageNight}N / ${pkg.packageDays}D`,
    nights: Number(pkg.packageNight) || 0,
    location: cities.length ? `${cities.join(" / ")} Departure` : pkg.routeName || "",
    searchText: `${pkg.packageName || ""} ${pkg.routeName || ""} ${cities.join(" ")}`.toLowerCase(),
    dates: shownDates,
    extraBatches: extraBatches || undefined,
    raw: pkg,
  };
}

// Raw `packagesList` object -> months array, each with its own trip list.
function buildMonthsFromApiData(packagesList) {
  if (!packagesList || typeof packagesList !== "object") return [];
  return Object.keys(packagesList)
    .sort((a, b) => monthKeyToDate(a) - monthKeyToDate(b))
    .map((monthKey) => ({
      id: monthKey,
      tabLabel: formatMonthTabLabel(monthKey),
      trips: (packagesList[monthKey] || []).map((pkg) => mapPackageToTrip(pkg, monthKey)),
    }));
}

const DURATION_BOUNDS = { min: 2, max: 16 };
const BUDGET_BOUNDS = { min: 8000, max: 400000 };

const DESTINATION_GROUPS = [
  {
    id: "india",
    label: "India",
    options: ["Kashmir", "Kerala", "Rajasthan", "Himachal", "Uttarakhand", "North East", "Andaman"],
  },
  {
    id: "international",
    label: "International",
    options: ["Europe", "Vietnam", "Thailand", "Dubai", "Bali", "Singapore", "Maldives"],
  },
];

/* ------------------------------------------------------------------ */
/* Small inline icons                                                  */
/* ------------------------------------------------------------------ */

const ClockIcon = () => (
  <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <circle cx="10" cy="10" r="7.25" stroke="currentColor" strokeWidth="1.4" />
    <path d="M10 6v4.2l2.6 1.6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const PinIcon = () => (
  <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path d="M10 18s6-5.2 6-9.8A6 6 0 1 0 4 8.2C4 12.8 10 18 10 18Z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
    <circle cx="10" cy="8.2" r="2" stroke="currentColor" strokeWidth="1.4" />
  </svg>
);

const CalendarIcon = () => (
  <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <rect x="3.25" y="4.25" width="13.5" height="12.5" rx="1.75" stroke="currentColor" strokeWidth="1.4" />
    <path d="M3.25 8h13.5M6.75 2.75v3M13.25 2.75v3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>
);

const ChevronIcon = ({ direction = "left" }) => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" style={{ transform: direction === "right" ? "rotate(180deg)" : "none" }}>
    <path d="M15 6l-6 6 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ChevronRightSmall = ({ expanded }) => (
  <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" style={{ transform: expanded ? "rotate(90deg)" : "none", transition: "transform .15s ease" }}>
    <path d="M7.5 4.5l5 5.5-5 5.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ExternalLinkIcon = () => (
  <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path d="M7 13 13 7M13 7H8M13 7v5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const FilterIcon = () => (
  <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path d="M3 5h14M6 10h8M8.5 15h3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

const CloseIcon = () => (
  <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path d="M5 5l10 10M15 5 5 15" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

/* ------------------------------------------------------------------ */
/* Dual-thumb range slider (Duration / Budget)                         */
/* ------------------------------------------------------------------ */

function DualRangeSlider({ min, max, step = 1, value, onChange, formatValue }) {
  const [lo, hi] = value;
  const pct = (v) => ((v - min) / (max - min)) * 100;

  const handleLo = (e) => {
    const next = Math.min(Number(e.target.value), hi - step);
    onChange([next, hi]);
  };
  const handleHi = (e) => {
    const next = Math.max(Number(e.target.value), lo + step);
    onChange([lo, next]);
  };

  return (
    <div className="utd-range">
      <div className="utd-range__track-wrap" style={{ "--lo": `${pct(lo)}%`, "--hi": `${pct(hi)}%` }}>
        <div className="utd-range__track" />
        <div className="utd-range__fill" />
        <input
          type="range"
          className="utd-range__input"
          min={min}
          max={max}
          step={step}
          value={lo}
          onChange={handleLo}
          aria-label="Minimum"
        />
        <input
          type="range"
          className="utd-range__input"
          min={min}
          max={max}
          step={step}
          value={hi}
          onChange={handleHi}
          aria-label="Maximum"
        />
      </div>
      <div className="utd-range__labels">
        <span>{formatValue(lo)}</span>
        <span>{formatValue(hi)}</span>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Trip card                                                           */
/* ------------------------------------------------------------------ */

function TripCard({ trip, onClick }) {
  const { image, title, originalPrice, price, duration, location, dates = [], extraBatches } = trip;

  return (
    <div
      className="utd-card"
      role="button"
      tabIndex={0}
      onClick={() => onClick?.(trip)}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onClick?.(trip); }}
    >
      <div className="utd-card__media">
        {image && <img src={image} alt={title} loading="lazy" />}
      </div>

      <div className="utd-card__body">
        <div className="utd-card__row">
          <h3 className="utd-card__title">{title}</h3>
          <button
            type="button"
            className="utd-card__link-btn"
            aria-label={`Open ${title}`}
            onClick={(e) => { e.stopPropagation(); onClick?.(trip); }}
          >
            <ExternalLinkIcon />
          </button>
        </div>

        <div className="utd-card__meta-row">
          <span className="utd-card__meta"><ClockIcon />{duration}</span>
          {location ? (
            <span className="utd-card__meta utd-card__meta--location">
              <PinIcon /><span className="utd-card__meta-text">{location}</span>
            </span>
          ) : null}
        </div>

        {dates.length > 0 && (
          <div className="utd-card__meta-row">
            <span className="utd-card__meta">
              <CalendarIcon />
              {dates.join(", ")}
              {extraBatches ? <span className="utd-card__extra-batches"> +{extraBatches} batches</span> : null}
            </span>
          </div>
        )}

        <div className="utd-card__price-row">
          {originalPrice ? <span className="utd-card__price-original">{formatINR(originalPrice)}</span> : null}
          <b className="utd-card__price-current">{formatINR(price)}</b>
          <span className="utd-card__price-suffix">Onwards</span>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Main page component                                                 */
/* ------------------------------------------------------------------ */

export default function UpcomingTripDetails({ data }) {
  const navigate = useNavigate();

  const [packagesList, setPackagesList] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (data) return;
    setIsLoading(true);
    axios({
      url: `${environment.config.apiBaseUrl}package/getUpcomingTourPackage`,
      method: "GET",
      headers: { authorization: "your token comes here" },
    })
      .then((res) => {
        if (res && res.data && res.data.packagesList) {
          setPackagesList(res.data.packagesList);
        }
      })
      .catch(() => { })
      .finally(() => setIsLoading(false));
  }, [data]);

  const months = useMemo(
    () => buildMonthsFromApiData(data || packagesList),
    [data, packagesList]
  );

  const [activeMonthId, setActiveMonthId] = useState(undefined);
  useEffect(() => {
    if (months.length && !months.some((m) => m.id === activeMonthId)) {
      setActiveMonthId(months[0].id);
    }
  }, [months, activeMonthId]);

  const activeMonth = months.find((m) => m.id === activeMonthId) || months[0];

  // ---- Filters (live) ----
  const [durationRange, setDurationRange] = useState([DURATION_BOUNDS.min, DURATION_BOUNDS.max]);
  const [budgetRange, setBudgetRange] = useState([BUDGET_BOUNDS.min, BUDGET_BOUNDS.max]);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [selectedKeywords, setSelectedKeywords] = useState([]);

  const toggleGroup = (groupId) =>
    setExpandedGroups((prev) => ({ ...prev, [groupId]: !prev[groupId] }));

  const toggleKeyword = (keyword) =>
    setSelectedKeywords((prev) =>
      prev.includes(keyword) ? prev.filter((k) => k !== keyword) : [...prev, keyword]
    );

  const clearFilters = () => {
    setDurationRange([DURATION_BOUNDS.min, DURATION_BOUNDS.max]);
    setBudgetRange([BUDGET_BOUNDS.min, BUDGET_BOUNDS.max]);
    setSelectedKeywords([]);
  };

  const filteredTrips = useMemo(() => {
    const trips = activeMonth?.trips || [];
    return trips.filter((trip) => {
      if (trip.nights < durationRange[0] || trip.nights > durationRange[1]) return false;
      if (trip.price < budgetRange[0] || trip.price > budgetRange[1]) return false;
      if (selectedKeywords.length) {
        const hit = selectedKeywords.some((kw) => trip.searchText.includes(kw.toLowerCase()));
        if (!hit) return false;
      }
      return true;
    });
  }, [activeMonth, durationRange, budgetRange, selectedKeywords]);

  // ---- Month tab horizontal scroll ----
  const tabsRef = useRef(null);
  const scrollTabs = (direction) => {
    const el = tabsRef.current;
    if (!el) return;
    el.scrollBy({ left: direction * 220, behavior: "smooth" });
  };

  // ---- Mobile filters drawer ----
  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false);

  const handleCardClick = (trip) => {
    navigate(`/package-details/:${trip.id}`);
  };

  return (
    <>
    <div className="stickyheader">
        <HeaderComponent/>
        <NewNavBar className='newnavbartoggle' />
      </div>
      <Sidebar className='sidebartoggle' />
       <div className="utd-page">
      {isMobileFiltersOpen && (
        <div className="utd-backdrop" onClick={() => setIsMobileFiltersOpen(false)} />
      )}

      <aside className={"utd-filters" + (isMobileFiltersOpen ? " utd-filters--open" : "")}>
        <div className="utd-filters__header">
          <h2>Filters</h2>
          <button
            type="button"
            className="utd-filters__close"
            aria-label="Close filters"
            onClick={() => setIsMobileFiltersOpen(false)}
          >
            <CloseIcon />
          </button>
        </div>

        <div className="utd-filters__body">
          <div className="utd-filter-group">
            <h3 className="utd-filter-group__title">Destination</h3>
            {DESTINATION_GROUPS.map((group) => (
              <div key={group.id} className="utd-destination">
                <button
                  type="button"
                  className="utd-destination__row"
                  onClick={() => toggleGroup(group.id)}
                >
                  <span>{group.label}</span>
                  <ChevronRightSmall expanded={!!expandedGroups[group.id]} />
                </button>
                {expandedGroups[group.id] && (
                  <div className="utd-destination__options">
                    {group.options.map((opt) => (
                      <button
                        type="button"
                        key={opt}
                        className={
                          "utd-chip" + (selectedKeywords.includes(opt) ? " utd-chip--active" : "")
                        }
                        onClick={() => toggleKeyword(opt)}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="utd-filter-group">
            <h3 className="utd-filter-group__title">Duration (in nights)</h3>
            <DualRangeSlider
              min={DURATION_BOUNDS.min}
              max={DURATION_BOUNDS.max}
              value={durationRange}
              onChange={setDurationRange}
              formatValue={(v) => `${v}N`}
            />
          </div>

          <div className="utd-filter-group">
            <h3 className="utd-filter-group__title">Budget (per person)</h3>
            <DualRangeSlider
              min={BUDGET_BOUNDS.min}
              max={BUDGET_BOUNDS.max}
              step={1000}
              value={budgetRange}
              onChange={setBudgetRange}
              formatValue={(v) => formatINR(v).replace("₹", "")}
            />
          </div>

          <div className="utd-filter-group">
            <h3 className="utd-filter-group__title">Month</h3>
            <div className="utd-month-checklist">
              {months.map((m) => (
                <label key={m.id} className="utd-checkbox">
                  <input
                    type="checkbox"
                    checked={m.id === activeMonthId}
                    onChange={() => setActiveMonthId(m.id)}
                  />
                  <span>{m.tabLabel}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="utd-filters__footer">
          <button type="button" className="utd-btn utd-btn--ghost" onClick={clearFilters}>
            Clear Filters
          </button>
          <button
            type="button"
            className="utd-btn utd-btn--primary"
            onClick={() => setIsMobileFiltersOpen(false)}
          >
            Apply
          </button>
        </div>
      </aside>

      <main className="utd-main">
        <div className="utd-month-tabs">
          <button type="button" className="utd-tab-arrow" aria-label="Scroll months left" onClick={() => scrollTabs(-1)}>
            <ChevronIcon direction="left" />
          </button>
          <div className="utd-tabs-track" ref={tabsRef}>
            {months.map((m) => (
              <button
                key={m.id}
                type="button"
                className={"utd-tab" + (m.id === activeMonthId ? " utd-tab--active" : "")}
                onClick={() => setActiveMonthId(m.id)}
              >
                {m.tabLabel}
              </button>
            ))}
          </div>
          <button type="button" className="utd-tab-arrow" aria-label="Scroll months right" onClick={() => scrollTabs(1)}>
            <ChevronIcon direction="right" />
          </button>
        </div>

        <button
          type="button"
          className="utd-mobile-filter-btn"
          onClick={() => setIsMobileFiltersOpen(true)}
        >
          <FilterIcon /> Filters
        </button>

        <h2 className="utd-heading">All Upcoming Trips</h2>

        {isLoading ? (
          <div className="utd-empty">Loading trips…</div>
        ) : filteredTrips.length > 0 ? (
          <div className="utd-grid">
            {filteredTrips.map((trip) => (
              <TripCard key={trip.id} trip={trip} onClick={handleCardClick} />
            ))}
          </div>
        ) : (
          <div className="utd-empty">
            No trips match your filters for {activeMonth?.tabLabel || "this month"}. Try
            widening your filters.
          </div>
        )}
      </main>
    </div>
    </>
   
  );
}

export { buildMonthsFromApiData };